import React from 'react';

// RFC 6455 "Service Restart". The audio websocket sends it both when a draining pod closes
// a live session and when it declines a new one, so the client needs one rule rather than
// one per situation: this pod is going away, try another.
export const DRAIN_CLOSE_CODE = 1012;

export const FAST_RECONNECT_INTERVAL_MS = 500;

// A drain refusal completes the handshake before closing, so react-use-websocket counts it
// as a successful connection and resets its own backoff — which is what we want, since a
// pod declining to take work is no reason to make the caller wait. The cost is that
// nothing escalates on its own: if every pod answered 1012, the client would retry twice a
// second forever. Past this many refusals in a row it starts backing off instead.
//
// A draining pod is expected to keep refusing for as long as it takes the load balancer to
// drop it (~10s, so ~20 refusals if the client keeps landing there), which is why the
// threshold sits well above that: crossing it means something fleet-wide, not one pod
// leaving.
export const MAX_CONSECUTIVE_DRAIN_REFUSALS = 40;

// A connection that lasted at least this long carried a real session, whatever code closed
// it, so the run of refusals is over.
export const ESTABLISHED_CONNECTION_MS = 5_000;

type DrainAwareReconnect = {
  onOpen: () => void;
  onClose: (event: CloseEvent) => void;
  reconnectInterval: (priorReconnectCount: number) => number;
};

/**
 * Keeps a client bouncing off draining pods from either stalling or spinning.
 *
 * A pod that is shutting down refuses new sessions with a websocket close code rather than
 * an HTTP status, because a browser is told nothing about a failed handshake — it cannot
 * tell 503 from 401 from a dropped network, and treating a refusal as a fault pushes the
 * reconnect backoff towards a minute for something that should be retried instantly
 * somewhere else. Reading the close code is what makes the difference visible here.
 */
export default function useDrainAwareReconnect(): DrainAwareReconnect {
  const consecutiveRefusalsRef = React.useRef(0);
  const openedAtRef = React.useRef<number | null>(null);

  const onOpen = React.useCallback(() => {
    openedAtRef.current = Date.now();
  }, []);

  const onClose = React.useCallback((event: CloseEvent) => {
    const openedAt = openedAtRef.current;
    openedAtRef.current = null;

    const carriedASession = openedAt !== null && Date.now() - openedAt >= ESTABLISHED_CONNECTION_MS;
    if (event.code === DRAIN_CLOSE_CODE && !carriedASession) {
      consecutiveRefusalsRef.current += 1;
      return;
    }
    consecutiveRefusalsRef.current = 0;
  }, []);

  const reconnectInterval = React.useCallback((priorReconnectCount: number) => {
    const refusals = consecutiveRefusalsRef.current;
    if (refusals > MAX_CONSECUTIVE_DRAIN_REFUSALS) {
      return Math.min(2 ** (refusals - MAX_CONSECUTIVE_DRAIN_REFUSALS) * 1000, 60000);
    }
    if (priorReconnectCount < 10) {
      return FAST_RECONNECT_INTERVAL_MS;
    }
    return Math.min(2 ** (priorReconnectCount - 10) * 1000, 60000);
  }, []);

  return { onOpen, onClose, reconnectInterval };
}
